// API client functions to replace Supabase client functions
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

/**
 * Check if the user is authenticated by making a request to the API
 * @returns {Promise<Object>} Authentication status and user data
 */
export const checkAuth = async () => {
  try {
    const sessionStr = localStorage.getItem('session') || localStorage.getItem('sb-ybjfvkqmqmxtbhzoygom-auth-token');
    //console.log('sessionStrrr', sessionStrrr);
    if (!sessionStr) {
      return { authenticated: false };
    }

    const session = JSON.parse(sessionStr);
    
    if (session.expires_at && session.expires_at < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem('session');
      return { authenticated: false };
    }

    const response = await fetch(`${API_URL}/api/v2/auth/verify`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      localStorage.removeItem('session');
      return { authenticated: false };
    }

    const data = await response.json();
    
    return {
      user: data.user,
      role: data.user.role,
      authenticated: true,
      vendor_id: data.id && data.type === 'vendor' ? data.id : null,
      org_id: data.id && data.type === 'organization' ? data.id : null
    };
  } catch (error) {
    console.error('Error checking authentication:', error);
    return { authenticated: false };
  }
};

/**
 * Create a user profile
 * @param {string} userId - User ID
 * @param {Object} data - User data
 * @returns {Promise<Object>} Result of the operation
 */
export const createUserProfile = async (userId, data) => {
  try {
    const response = await fetch(`${API_URL}/api/users/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: userId,
        role: 'customer',
        ...data,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create user profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

/**
 * Create a vendor profile
 * @param {string} userId - User ID
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Result of the operation
 */
export const createVendorProfile = async (userId, userData) => {
  try {
    const response = await fetch(`${API_URL}/api/vendors/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        email: userData.email,
        phone_number: userData.contact_phone,
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_name: userData.company_name,
        contact_email: userData.email,
        contact_phone: userData.contact_phone,
        address: userData.address,
        type: userData.type,
        mascotname: userData.mascotname,
        short_name: userData.short_name,
        secondary_color: "#1c4ed8",
        primary_color: "#1f3a8a",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create vendor profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

/**
 * Create an organization profile
 * @param {string} userId - User ID
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Result of the operation
 */
export const createOrganizationProfile = async (userId, userData) => {
  try {
    const response = await fetch(`${API_URL}/api/organizations/profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        user_id: userId,
        email: userData.email,
        phone_number: userData.contact_phone,
        first_name: userData.first_name,
        last_name: userData.last_name,
        company_name: userData.company_name,
        contact_email: userData.email,
        contact_phone: userData.contact_phone,
        address: userData.address,
        type: userData.type,
        primary_color: "#1f3a8a",
        secondary_color: "#1c4ed8",
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return { error: new Error(errorData.message || 'Failed to create organization profile') };
    }

    return { error: null };
  } catch (error) {
    return { error };
  }
};

/**
 * Helper function for public API requests - no auth token needed
 * @param {string} url - API URL to fetch from
 * @returns {Promise<Object>} API response
 */
const publicFetch = async (url, options = {}) => {
  try {
    const response = await fetch(`${API_URL}${url}`, {
      method: options.method || 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...(options.body && { body: options.body }),
    });

    if (!response.ok) {
      throw new Error(`API error: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
};

/**
 * Get all public events
 * @returns {Promise<Object>}
 */
export const fetchPublicEvents = async () => {
  try {
    const data = await publicFetch('/api/public/events');
    return { data: data.events, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get public event details by ID
 * @param {string} eventId 
 * @returns {Promise<Object>}
 */
export const fetchPublicEventById = async (eventId) => {
  try {
    const data = await publicFetch(`/api/public/events/${eventId}`);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get all public vendors
 * @returns {Promise<Object>} Public vendors data
 */
export const fetchPublicVendors = async () => {
  try {
    const data = await publicFetch('/api/public/vendors');
    return { data: data.vendors, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get public vendor details by ID
 * @param {string} vendorId - Vendor ID
 * @returns {Promise<Object>} Vendor details and upcoming events
 */
export const fetchPublicVendorById = async (vendorId) => {
  try {
    const data = await publicFetch(`/api/public/vendors/${vendorId}`);
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Helper function for authenticated API requests - adds auth token and handles expired tokens
 * @param {string} url - API URL to fetch from
 * @param {Object} options - Fetch options like method, body, etc.
 * @returns {Promise<Object>} API response
 */
export const authenticatedFetch = async (url, options = {}) => {
  try {
    // Get the session from localStorage
    const sessionStr = localStorage.getItem('session');
    if (!sessionStr) {
      throw new Error('No active session found');
    }
    
    const session = JSON.parse(sessionStr);
    
    // Check if the session is expired
    if (session.expires_at && session.expires_at < Math.floor(Date.now() / 1000)) {
      localStorage.removeItem('session');
      throw new Error('Session expired');
    }
    
    // Merge provided options with auth headers
    const fetchOptions = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        ...(options.headers || {})
      }
    };
    
    const response = await fetch(`${API_URL}${url}`, fetchOptions);
    
    // Handle 401 Unauthorized errors (expired token)
    if (response.status === 401) {
      localStorage.removeItem('session');
      throw new Error('Session expired or invalid');
    }
    
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `API error: ${response.statusText}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error(`Error with authenticated request to ${url}:`, error);
    throw error;
  }
};



/**
 * Get public organization details by ID
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Organization details
 */
export const fetchPublicOrganizationById = async (organizationId) => {
  try {
    const data = await publicFetch(`/api/public/organizations/${organizationId}`);
    return { data: data.organization, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get vendors under a specific organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Vendors data
 */
export const fetchPublicOrganizationVendors = async (organizationId) => {
  try {
    const data = await publicFetch(`/api/public/organizations/${organizationId}/vendors`);
    return { data: data.vendors, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get upcoming events for all vendors in an organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Events data
 */
export const fetchPublicOrganizationEvents = async (organizationId) => {
  try {
    const data = await publicFetch(`/api/public/organizations/${organizationId}/events`);
    return { data: data.events, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get all public organizations
 * @returns {Promise<Object>} Public organizations data
 */
export const fetchPublicOrganizations = async () => {
  try {
    const data = await publicFetch('/api/public/organizations');
    return { data: data.organizations, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Unified search function that searches across organizations, vendors, and events
 * @param {string} query - Search query (must be at least 2 characters)
 * @returns {Promise<Object>} Search results with organizations, vendors, and events
 */
export const searchAll = async (query) => {
  try {
    if (!query || query.length < 2) {
      return { 
        data: { 
          organizations: [], 
          vendors: [], 
          events: [] 
        }, 
        error: null 
      };
    }
    
    const encodedQuery = encodeURIComponent(query);
    const data = await publicFetch(`/api/public/search?query=${encodedQuery}`);
    return { data, error: null };
  } catch (error) {
    console.error('Search error:', error);
    return { 
      data: { organizations: [], vendors: [], events: [] }, 
      error 
    };
  }
};

/**
 * Get vendors for an organization with their events and ticket sales data
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Vendors with events and sales data
 */
export const fetchOrganizationVendorsWithData = async (organizationId) => {
  try {
    const data = await authenticatedFetch(`/api/v2/organizations/${organizationId}/vendors/detailed`, {
      method: 'GET'
    });
    return { data: data.vendors, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get basic vendors for an organization 
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Basic vendor list
 */
export const fetchOrganizationVendors = async (organizationId) => {
  try {
    const data = await authenticatedFetch(`/api/v2/organizations/${organizationId}/vendors`, {
      method: 'GET'
    });
    return { data: data.vendors, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get vendor details by ID within organization context
 * @param {string} organizationId - Organization ID
 * @param {string} vendorId - Vendor ID
 * @returns {Promise<Object>} Vendor details
 */
export const fetchOrganizationVendorById = async (organizationId, vendorId) => {
  try {
    const data = await authenticatedFetch(`/api/v2/organizations/${organizationId}/vendors/${vendorId}`, {
      method: 'GET'
    });
    return { data: data.vendor, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Get vendor report data for custom date range within organization context
 * @param {string} organizationId - Organization ID
 * @param {string} vendorId - Vendor ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Promise<Object>} Report data with events and tickets
 */
export const fetchOrganizationVendorReportData = async (organizationId, vendorId, startDate, endDate) => {
  try {
    const params = new URLSearchParams({
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    });
    
    const data = await authenticatedFetch(`/api/v2/organizations/${organizationId}/vendors/${vendorId}/report?${params}`, {
      method: 'GET'
    });
    return { data, error: null };
  } catch (error) {
    return { data: null, error };
  }
};

/**
 * Check Stripe account status for a vendor
 * @param {string} vendorId - Vendor ID
 * @returns {Promise<Object>} Stripe account status and onboarding link if needed
 */
export const checkVendorStripeStatus = async (vendorId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/stripe/account-status/vendor/${vendorId}`);
    return {
      data: {
        status: response.status,
        isComplete: response.isComplete,
        accountLink: response.accountLink,
        requirements: response.requirements
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking vendor Stripe status:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Create or get Stripe account for a vendor
 * @param {string} vendorId - Vendor ID
 * @param {string} email - Vendor email
 * @param {string} businessName - Business name
 * @returns {Promise<Object>} Stripe account creation result with onboarding URL
 */
export const createVendorStripeAccount = async (vendorId, email, businessName) => {
  try {
    const response = await authenticatedFetch('/api/v2/stripe/create-account', {
      method: 'POST',
      body: JSON.stringify({
        accountType: 'vendor',
        entityId: vendorId,
        email,
        businessName
      })
    });
    return {
      data: {
        url: response.url,
        status: response.status
      },
      error: null
    };
  } catch (error) {
    console.error('Error creating vendor Stripe account:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Check Stripe account status for an organization
 * @param {string} organizationId - Organization ID
 * @returns {Promise<Object>} Stripe account status and onboarding link if needed
 */
export const checkOrganizationStripeStatus = async (organizationId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/stripe/account-status/organization/${organizationId}`);
    return {
      data: {
        status: response.status,
        isComplete: response.isComplete,
        accountLink: response.accountLink,
        requirements: response.requirements
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking organization Stripe status:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Create or get Stripe account for an organization
 * @param {string} organizationId - Organization ID
 * @param {string} email - Organization email
 * @param {string} businessName - Business name
 * @returns {Promise<Object>} Stripe account creation result with onboarding URL
 */
export const createOrganizationStripeAccount = async (organizationId, email, businessName) => {
  try {
    const response = await authenticatedFetch('/api/v2/stripe/create-account', {
      method: 'POST',
      body: JSON.stringify({
        accountType: 'organization',
        entityId: organizationId,
        email,
        businessName
      })
    });
    return {
      data: {
        url: response.url,
        status: response.status
      },
      error: null
    };
  } catch (error) {
    console.error('Error creating organization Stripe account:', error);
    return { data: null, error: error.message };
  }
};

// ===== TICKET PURCHASE ENDPOINTS =====

/**
 * Get authenticated user profile data
 * @returns {Promise<Object>} User profile data
 */
export const getUserProfile = async () => {
  try {
    const response = await authenticatedFetch('/api/v2/auth/profile');
    return {
      data: {
        user: response.user,
        profile: response.profile
      },
      error: null
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Fetch all tickets for the same event and email as the specified ticket
 * @param {string} ticketId - The main ticket ID
 * @returns {Promise<Object>} Event tickets data with organization info
 */
export const fetchEventTicketsByTicketId = async (ticketId) => {
  try {
    const response = await publicFetch(`/api/v2/tickets/${ticketId}/event-tickets`);
    return {
      data: {
        tickets: response.tickets,
        organizationTicketType: response.organizationTicketType
      },
      error: null
    };
  } catch (error) {
    console.error('Error fetching event tickets:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Check in a specific ticket (public endpoint for self-checkin)
 * @param {string} ticketId - Ticket ID to check in
 * @returns {Promise<Object>} Check-in result
 */
export const checkInTicketSelf = async (ticketId) => {
  try {
    const response = await publicFetch(`/api/v2/tickets/${ticketId}/checkin`, {
      method: 'PATCH'
    });
    return {
      data: {
        checked_in: response.checked_in,
        checked_in_at: response.checked_in_at
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking in ticket:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Fetch all tickets for authenticated user
 * @returns {Promise<Object>} User tickets data
 */
export const fetchUserTickets = async () => {
  try {
    const response = await authenticatedFetch('/api/v2/auth/user-tickets');
    return {
      data: {
        upcoming: response.upcoming,
        past: response.past
      },
      error: null
    };
  } catch (error) {
    console.error('Error fetching user tickets:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Check event capacity and availability
 * @param {string} eventId - Event ID
 * @returns {Promise<Object>} Event capacity data
 */
export const checkEventCapacity = async (eventId) => {
  try {
    const response = await publicFetch(`/api/v2/events/${eventId}/capacity`);
    return {
      data: {
        soldTickets: response.soldTickets,
        maxAttendees: response.maxAttendees,
        availableTickets: response.availableTickets
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking event capacity:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Validate ticket purchase before processing
 * @param {string} eventId - Event ID
 * @param {Object} ticketQuantities - Ticket quantities to purchase
 * @returns {Promise<Object>} Validation result
 */
export const validateTicketPurchase = async (eventId, ticketQuantities) => {
  try {
    const response = await publicFetch(`/api/v2/events/${eventId}/validate-purchase`, {
      method: 'POST',
      body: JSON.stringify({ ticketQuantities })
    });
    return {
      data: {
        valid: response.valid,
        availableTickets: response.availableTickets,
        message: response.message
      },
      error: null
    };
  } catch (error) {
    console.error('Error validating ticket purchase:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Fetch tickets for an organization with pagination and filtering
 * @param {string} organizationId - Organization ID
 * @param {Object} options - Query options
 * @param {number} options.page - Page number (default: 1)
 * @param {number} options.limit - Items per page (default: 20)
 * @param {string} options.search - Search term
 * @param {string} options.vendorId - Filter by vendor ID
 * @param {string} options.eventId - Filter by event ID
 * @param {string} options.checkedInStatus - Filter by check-in status ('all', 'checked-in', 'not-checked-in')
 * @param {string} options.dateFrom - Filter by purchase date from
 * @param {string} options.dateTo - Filter by purchase date to
 * @returns {Promise<Object>} Organization tickets data with pagination
 */
export const fetchOrganizationTickets = async (organizationId, options = {}) => {
  try {
    const {
      page = 1,
      limit = 20,
      search = '',
      vendorId = '',
      eventId = '',
      checkedInStatus = 'all',
      dateFrom = '',
      dateTo = ''
    } = options;

    // Build query parameters
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });

    // Add optional filters
    if (search) params.append('search', search);
    if (vendorId) params.append('vendorId', vendorId);
    if (eventId) params.append('eventId', eventId);
    if (checkedInStatus !== 'all') params.append('checkedIn', checkedInStatus === 'checked-in' ? 'true' : 'false');
    if (dateFrom) params.append('dateFrom', dateFrom);
    if (dateTo) params.append('dateTo', dateTo);

    const response = await authenticatedFetch(`/api/v2/organizations/${organizationId}/tickets?${params.toString()}`);
    // The backend returns { data: [...], pagination: {...}, filters: {...} }
    return { 
      data: response.data, 
      pagination: response.pagination,
      filters: response.filters,
      error: null 
    };
  } catch (error) {
    console.error('Error fetching organization tickets:', error);
    return { data: null, pagination: null, filters: null, error: error.message };
  }
};

/**
 * Check in a ticket
 * @param {string} ticketId - Ticket ID
 * @returns {Promise<Object>} Check-in result
 */
export const checkInTicket = async (ticketId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/tickets/${ticketId}/check-in`, {
      method: 'PATCH',
      body: JSON.stringify({
        checked_in: true
      })
    });
    // The backend returns { data: {...} }, so we need to extract the data object
    return { data: response.data, error: null };
  } catch (error) {
    console.error('Error checking in ticket:', error);
    return { data: null, error: error.message };
  }
};

// ===== SECURE STRIPE ENDPOINTS =====

/**
 * Create a payment intent securely on the backend
 * @param {string} vendorId - Vendor ID
 * @param {Array} tickets - Array of ticket objects
 * @param {Object} ticketQuantities - Object mapping ticket IDs to quantities
 * @returns {Promise<Object>} Payment intent data
 */
export const createPaymentIntent = async (vendorId, tickets, ticketQuantities) => {
  try {
    const response = await publicFetch('/api/v2/stripe/payment-intent', {
      method: 'POST',
      body: JSON.stringify({
        vendorId,
        tickets,
        ticketQuantities
      })
    });

    return {
      clientSecret: response.clientSecret,
      paymentIntentId: response.paymentIntentId,
      totalAmount: response.totalAmount,
      breakdown: response.breakdown
    };
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw new Error(error.message || 'Failed to create payment intent');
  }
};

/**
 * Check Stripe account status securely
 * @param {string} accountType - 'vendor' or 'organization'
 * @param {string} entityId - Entity ID (vendor or organization ID)
 * @returns {Promise<Object>} Account status data
 */
export const checkStripeAccountStatus = async (accountType, entityId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/stripe/account-status/${accountType}/${entityId}`);

    return {
      data: {
        status: response.status,
        isComplete: response.isComplete,
        accountLink: response.accountLink,
        requirements: response.requirements
      },
      error: null
    };
  } catch (error) {
    console.error('Error checking Stripe account status:', error);
    return { data: null, error: error.message };
  }
};

/**
 * Create Stripe account securely
 * @param {string} accountType - 'vendor' or 'organization'
 * @param {string} entityId - Entity ID (vendor or organization ID)
 * @param {string} email - Account email
 * @param {string} businessName - Business name
 * @returns {Promise<Object>} Account creation result
 */
export const createStripeAccount = async (accountType, entityId, email, businessName) => {
  try {
    const response = await authenticatedFetch('/api/v2/stripe/create-account', {
      method: 'POST',
      body: JSON.stringify({
        accountType,
        entityId,
        email,
        businessName
      })
    });

    return {
      url: response.url,
      status: response.status
    };
  } catch (error) {
    console.error('Error creating Stripe account:', error);
    throw new Error(error.message || 'Failed to create Stripe account');
  }
};

// Settings-related API functions
export const fetchTicketTypes = async () => {
  try {
    const response = await publicFetch('/api/v2/ticket-types');
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const fetchVendorData = async (vendorId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/vendors/${vendorId}`);
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const updateVendorData = async (vendorId, updateData) => {
  try {
    const response = await authenticatedFetch(`/api/v2/vendors/${vendorId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateData)
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const uploadLogo = async (file, entityType, entityId) => {
  try {
    const formData = new FormData();
    formData.append('logo', file);
    formData.append('entityType', entityType); // 'vendor' or 'organization'
    formData.append('entityId', entityId);

    const response = await authenticatedFetch('/api/v2/upload/logo', {
      method: 'POST',
      body: formData // Don't set Content-Type header for FormData
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// Additional API functions for remaining components
export const fetchVendorBasicData = async (vendorId) => {
  try {
    const response = await authenticatedFetch(`/api/v2/vendors/${vendorId}/basic`);
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const fetchVendorReportData = async (vendorId, filters) => {
  try {
    const queryParams = new URLSearchParams({
      startDate: filters.startDate.toISOString(),
      endDate: filters.endDate.toISOString(),
      ...(filters.eventType && { eventType: filters.eventType })
    });
    
    const response = await authenticatedFetch(`/api/v2/vendors/${vendorId}/reports?${queryParams}`);
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const fetchUserProfile = async () => {
  try {
    const response = await authenticatedFetch('/api/v2/auth/profile');
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const updateUserProfile = async (field, value) => {
  try {
    const response = await authenticatedFetch('/api/v2/auth/profile', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ [field]: value })
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const checkUserExists = async (email) => {
  try {
    const response = await publicFetch(`/api/v2/users/check-exists?email=${encodeURIComponent(email)}`);
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

// OTP Authentication API functions
export const sendLoginOTP = async (email) => {
  try {
    const response = await publicFetch('/api/v2/otp/send-login-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const verifyLoginOTP = async (email, otp) => {
  try {
    const response = await publicFetch('/api/v2/otp/verify-login-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp })
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const sendPasswordResetOTP = async (email) => {
  try {
    const response = await publicFetch('/api/v2/otp/send-reset-code', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};

export const resetPasswordWithOTP = async (email, otp, newPassword) => {
  try {
    const response = await publicFetch('/api/v2/otp/reset-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp, newPassword })
    });
    return { data: response, error: null };
  } catch (error) {
    return { data: null, error: error.message };
  }
};



 